import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WORKFLOW_SETUP_ADMIN_ROUTE } from "@/components/WorkflowSetupRequired";
import { Wrench, ArrowRight } from "lucide-react";

// "Existing Case Workflow Setup" (2026-09) — the business-facing way an
// administrator discovers and resolves Cases created before the new
// workflow. It replaces the permanent Administration tab: an admin-only
// banner on Process / Cases (shown ONLY while such Cases exist) opens the
// review screen at WORKFLOW_SETUP_ADMIN_ROUTE (pages/CaseWorkflowSetupPage).
//
// Backend contract (unchanged; all admin.config):
//   GET  /admin/case-adoption/setup-summary  -> banner count (read-only)
//   GET  /admin/case-adoption/setup-review   -> the Cases to review (read-only)
//   POST /admin/case-adoption/apply          -> confirm (typed phrase, every
//                                               selection re-validated fresh)
//   GET  /admin/case-adoption/adoptions | verify, POST rollback -> history/undo

export const SETUP_CAP = "admin.config";

// The exact phrase POST /apply requires (routers_case_adoption.apply_phrase).
export const confirmPhraseFor = (n) => `ADOPT ${n} CASE${n === 1 ? "" : "S"}`;
// The exact phrase POST /rollback requires.
export const undoPhraseFor = (caseId) => `ROLLBACK ${caseId}`;

export const casesNeedSetupText = (n) =>
  `${n} existing Case${n === 1 ? " needs" : "s need"} workflow setup`;

// Every reason code migrations/legacy_case_adoption.classify_case can emit
// for a Case still needing setup, in business language. The backend's own
// message (which names internal mechanisms) is only a last-resort fallback
// for a code added later.
export function reasonText(reason, row) {
  const cur = row?.current_stage;
  switch (reason?.code) {
    case "stage_maps":
      return "The recorded stage matches a stage in the new workflow.";
    case "stage_mapped":
      return `'${cur}' is an older stage name — in the new workflow it is '${row?.proposed_stage}'.`;
    case "stage_not_in_registry":
      return `'${cur}' isn't a stage in the new ${row?.workflow_label ? `${row.workflow_label} ` : ""}workflow, so it can't be matched automatically.`;
    case "commercial_signup_not_approved":
      return "Leadership hasn't approved this customer's Signup, so a stage after Signup can't be confirmed yet.";
    case "visa_payment_gate_unpaid":
      return "The payment required before Visa processing hasn't been received yet.";
    case "milestone_payment_past_stage":
      return "A verified payment suggests this Case may be further along than its recorded stage. A stage is never moved forward from a payment — confirm where it really is.";
    case "milestone_recorded_past_stage":
      return "A finance milestone on this customer's plan is later than the recorded stage — confirm where the Case really is.";
    case "system_managed_stage_without_approval":
      return `'${row?.suggested_stage || cur}' only ends with a Leadership Signup approval, and there isn't one for this customer — choose the stage the Case is really at.`;
    case "missing_contact":
      return "This Case isn't linked to a customer record — link it before setting it up.";
    default:
      return reason?.message || "";
  }
}

// What confirming a stage will create: the first task(s) and the Next Action.
// `tasks` = entry_task_plan() rows, `next` = predict_next_action().
export function planSummary(tasks, next) {
  const list = tasks || [];
  return {
    tasks: list.map((t) => ({
      title: t.title,
      optional: !t.mandatory,
      waitsForOwner: !t.will_create && t.waits_for === "process_owner",
    })),
    nextAction: next?.title || null,
  };
}

// Admin-only banner for Process / Cases. Renders NOTHING for non-admins, while
// loading, on error, or once no Case needs setup — it is re-fetched every time
// the Process page mounts (i.e. on every navigation back to it, including
// straight after confirming Cases on the review screen).
export function WorkflowSetupBanner() {
  const { hasCap } = useAuth();
  const isAdmin = hasCap(SETUP_CAP);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    api.get("/admin/case-adoption/setup-summary")
      .then((r) => { if (live) setSummary(r.data); })
      .catch(() => { if (live) setSummary(null); });
    return () => { live = false; };
  }, [isAdmin]);
  const n = summary?.needs_setup || 0;
  if (!isAdmin || n <= 0) return null;
  return (
    <Link to={WORKFLOW_SETUP_ADMIN_ROUTE} data-testid="workflow-setup-banner" data-count={n}
      className="flex items-center gap-3 rounded-md border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 px-3 py-2.5 hover:bg-[hsl(var(--warning))]/15 transition-colors">
      <Wrench className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold" data-testid="workflow-setup-banner-title">{casesNeedSetupText(n)}</p>
        <p className="text-xs text-muted-foreground">
          They were created before the new workflow. Confirm their current stage so mandatory tasks,
          outcomes and Next Actions can begin.
        </p>
      </div>
      <span className="text-xs font-medium text-primary flex items-center gap-1 shrink-0">
        Review <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
